// src/features/calendar/ImportExportDialog.tsx
// Move calendars in or out of other tools — iCalendar import and export.

import { useState } from 'react';
import { observer } from '@react';
import { useCalendar, useI18n } from '@react/hooks';
import { Button, Alert, Textarea } from '@react/components';
import { Select } from '@/components/Select';
import { useScope } from '@/hooks/useScope';
import type { Calendar } from '@core/types/calendar';
import { ModalShell } from './ModalShell';
import { errMessage, makeEntity } from './time';

interface ImportExportDialogProps {
  open: boolean;
  mode: 'import' | 'export';
  calendars: Calendar[];
  onClose: () => void;
  onImported?: () => void;
}

export const ImportExportDialog = observer(({ open, mode, calendars, onClose, onImported }: ImportExportDialogProps) => {
  const { t } = useI18n();
  const scope = useScope();
  const store = useCalendar();

  const [calendarId, setCalendarId] = useState('');
  const [icalData, setIcalData] = useState('');
  const [overwrite, setOverwrite] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [summary, setSummary] = useState<string | null>(null);

  const doImport = async () => {
    if (!calendarId || !icalData.trim()) return;
    setBusy(true);
    setError(null);
    setSummary(null);
    try {
      const res = await store.ImportCalendar(calendarId, {
        calendar: makeEntity(calendarId, 'calendar', scope),
        ical_data: icalData,
        overwrite_existing: overwrite,
      });
      setSummary(t('calendar.importExport.importResult', {
        imported: res.events_imported ?? 0,
        skipped: res.events_skipped ?? 0,
        updated: res.events_updated ?? 0,
      }));
      onImported?.();
    } catch (e) {
      setError(errMessage(e, t('calendar.importExport.importError')));
    } finally {
      setBusy(false);
    }
  };

  const doExport = async () => {
    if (!calendarId) return;
    setBusy(true);
    setError(null);
    try {
      const res = await store.ExportCalendar(calendarId);
      const blob = new Blob([res.ical_data ?? ''], { type: 'text/calendar' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'calendar.ics';
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(errMessage(e, t('calendar.importExport.exportError')));
    } finally {
      setBusy(false);
    }
  };

  return (
    <ModalShell
      open={open}
      onClose={onClose}
      title={mode === 'import' ? t('calendar.importExport.importTitle') : t('calendar.importExport.exportTitle')}
      footer={
        <>
          <Button variant="ghost" size="sm" onClick={onClose} disabled={busy}>
            {t('common.close')}
          </Button>
          {mode === 'import' ? (
            <Button size="sm" onClick={doImport} isLoading={busy} disabled={!calendarId || !icalData.trim()}>
              {t('calendar.importExport.importAction')}
            </Button>
          ) : (
            <Button size="sm" onClick={doExport} isLoading={busy} disabled={!calendarId}>
              {t('calendar.importExport.exportAction')}
            </Button>
          )}
        </>
      }
    >
      <div className="space-y-3">
        {error && <Alert variant="error">{error}</Alert>}
        {summary && <Alert variant="success">{summary}</Alert>}
        <Select
          label={t('calendar.event.calendar')}
          value={calendarId}
          onChange={(e) => setCalendarId(e.target.value)}
          placeholder={t('calendar.event.calendarPlaceholder')}
          options={calendars.map((c) => ({ value: c.entity?.id ?? '', label: c.name }))}
        />
        {mode === 'import' && (
          <>
            <label className="block text-sm font-medium text-foreground">
              {t('calendar.importExport.pasteOrUpload')}
              <input
                type="file"
                accept=".ics,text/calendar"
                className="mt-1 block w-full text-sm text-muted-foreground"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  file.text().then(setIcalData).catch(() => {});
                }}
              />
            </label>
            <Textarea
              label={t('calendar.importExport.icalData')}
              value={icalData}
              onChange={(e) => setIcalData(e.target.value)}
              rows={8}
              placeholder="BEGIN:VCALENDAR…"
            />
            <label className="flex items-center gap-2 text-sm text-foreground">
              <input type="checkbox" checked={overwrite} onChange={(e) => setOverwrite(e.target.checked)} />
              {t('calendar.importExport.overwrite')}
            </label>
          </>
        )}
        {mode === 'export' && (
          <p className="text-sm text-muted-foreground">{t('calendar.importExport.exportHint')}</p>
        )}
      </div>
    </ModalShell>
  );
});
